import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import toast from 'react-hot-toast'

// 🔐 Authentication
import { Login } from './pages/auth/Login'
import AuthService from './services/authService'

// 🏗️ Layouts
import { AdminLayout } from './components/layout/AdminLayout'
import { POSLayout } from './components/layout/POSLayout'

// 📊 Admin Pages
import { Dashboard } from './pages/admin/dashboard/Dashboard'
import { Sales } from './pages/admin/sales/Sales'
import AllProducts from './pages/admin/products/AllProducts'
import Suppliers from './pages/admin/suppliers/Suppliers'
import { Customers } from './pages/admin/customers/Customers'
import { CreditCustomers } from './pages/admin/customers/CreditCustomers'
import { CustomerGroups } from './pages/admin/customers/CustomerGroups'
// import { Promotions } from './pages/admin/promotions/Promotions'
import Reports from './pages/reports/EnhancedReports'

// Individual Report Pages
import SalesSummaryReport from './pages/reports/SalesSummaryReport'
import PaymentsReport from './pages/reports/PaymentsReport'
import ProductSummaryReport from './pages/reports/ProductSummaryReport'
import RawMaterialSummaryReport from './pages/reports/RawMaterialSummaryReport'
import ProductMarginReport from './pages/reports/ProductMarginReport'
import StockAlertsReport from './pages/reports/StockAlertsReport'
import SuppliersReport from './pages/reports/SuppliersReport'
import CustomersReport from './pages/reports/CustomersReport'
import EmployeesReport from './pages/reports/EmployeesReport'
import SalaryReports from './pages/reports/SalaryReports'
import ProductSalesReport from './pages/admin/reports/ProductSalesReport'
import StockExpiryReport from './pages/admin/reports/StockExpiryReport'
import CustomerReturnsReport from './pages/reports/CustomerReturnsReport'
import LeaveReports from './pages/reports/LeaveReports'
import SupplierReturnsReport from './pages/admin/reports/SupplierReturnsReport'
import WastageReport from './pages/reports/WastageReport'
import ProfitReport from './pages/reports/ProfitReport'
import ServiceChargeReport from './pages/admin/reports/ServiceChargeReport'
import ProductWiseSalesReport from './pages/admin/reports/ProductWiseSalesReport'
import NegativeStockReport from './pages/admin/reports/NegativeStockReport'
import AnalyticsDashboard from './pages/admin/analytics/AnalyticsDashboard'
import { Warehouse } from './pages/admin/warehouse/Warehouse'
import { Branches } from './pages/admin/roles/Branches'
import { Users } from './pages/admin/roles/Users'
import { Categories } from './pages/admin/products/Categories'
import Units from './pages/admin/products/Units'
import { RawMaterials } from './pages/admin/products/RawMaterials'
import { RawMaterialsInventory } from './pages/admin/products/RawMaterialsInventory'
import BranchProducts from './pages/admin/products/BranchProducts'
import Recipes from './pages/admin/products/Recipes'
import AllPurchaseOrders from './pages/admin/purchase-order/AllPurchaseOrders'
import CreatePurchaseOrder from './pages/admin/purchase-order/CreatePurchaseOrder'
import AllGRN from './pages/admin/grn/AllGRN'
import CreateGRN from './pages/admin/grn/CreateGRN'
import GRNPayments from './pages/admin/grn/GRNPayments'
import StockAlerts from './pages/admin/products/StockAlerts'
import BatchInventoryManagement from './pages/admin/products/BatchInventoryManagement'
import VIPRooms from './pages/admin/products/VIPRooms'
import { Tables } from './pages/admin/tables/Tables'
import AllPromotions from './pages/admin/promotions/AllPromotions'
import CreatePromotion from './pages/admin/promotions/CreatePromotion'
import EditPromotion from './pages/admin/promotions/EditPromotion'
import AllSMSCampaigns from './pages/admin/promotions/AllSMSCampaigns'
import JobManagement from './pages/admin/JobManagement'
import EmployeeManagement from './pages/admin/EmployeeManagement'
import EmployeeShifts from './pages/admin/hr/EmployeeShifts'
import LeaveManagement from './pages/admin/hr/LeaveManagement'
import Attendance from './pages/admin/hr/Attendance'
import PayrollManagement from './pages/admin/hr/PayrollManagement'
import TransportClaims from './pages/admin/hr/TransportClaims'
import ShiftManagement from './pages/admin/ShiftManagement'
import ProductsTransfer from './pages/admin/warehouse/ProductsTransfer'
import WarehouseProductions from './pages/admin/warehouse/WarehouseProductions'
import SupplierReturn from './pages/admin/returns/SupplierReturn'
import CustomerReturn from './pages/admin/returns/CustomerReturn'
import ConfigSettings from './pages/ConfigSettings'

// 🏪 POS Pages 

// 👨‍🍳 Barista Pages
import BaristaScreen from './pages/barista/BaristaScreen'
import BaristaDemo from './components/barista/BaristaDemo'

// 🧾 Public Pages
import EReceiptView from './pages/EReceiptView'

// 🎨 Global Styles
import './index.css'

// Make toast available globally for API error handling
window.toast = toast

// 🔐 LoginRoute component that handles redirection for authenticated users
const LoginRoute = ({ isAuthenticated, user, onLogin }) => {
  const navigate = useNavigate()

  // If already authenticated, redirect to appropriate dashboard
  useEffect(() => {
    if (isAuthenticated && user) {
      const userRole = user.role
      if (userRole === 'admin' || userRole === 'super_admin' || userRole === 'superadmin') {
        navigate('/admin/dashboard', { replace: true })
      } else if (userRole === 'manager') {
        navigate('/admin/dashboard', { replace: true })
      } else if (userRole === 'warehouse') {
        navigate('/admin/dashboard', { replace: true })
      } else if (userRole === 'barista') {
        navigate('/barista', { replace: true })
      } else if (userRole === 'cashier') {
        navigate('/pos', { replace: true })
      } else {
        // Default fallback
        navigate('/pos', { replace: true })
      }
    }
  }, [isAuthenticated, user, navigate])

  // If not authenticated, show login form
  if (!isAuthenticated) {
    return <Login onLogin={onLogin} />
  }

  // Return null while redirecting
  return null
}

// 🔐 AdminRoute component that handles role-based access to admin routes
const AdminRoute = ({ user, onLogout, onBranchSelect }) => {
  const navigate = useNavigate()

  // Check if user has admin access
  useEffect(() => {
    if (user) {
      const userRole = user.role
      // Only admin, super_admin, manager, and warehouse can access admin routes
      if (userRole === 'admin' || userRole === 'super_admin' || userRole === 'superadmin' || userRole === 'manager' || userRole === 'warehouse') {
        // User has access, let them proceed
        return
      } else {
        // Redirect non-admin users to their appropriate interface
        if (userRole === 'barista') {
          navigate('/barista', { replace: true })
        } else if (userRole === 'cashier') {
          navigate('/pos', { replace: true })
        } else {
          // Default fallback
          navigate('/pos', { replace: true })
        }
      }
    }
  }, [user, navigate])

  // If user has admin access, render AdminLayout
  const userRole = user?.role
  if (userRole === 'admin' || userRole === 'super_admin' || userRole === 'superadmin' || userRole === 'manager' || userRole === 'warehouse') {
    return <AdminLayout user={user} onLogout={onLogout} onBranchSelect={onBranchSelect} />
  }

  // Return null while redirecting
  return null
}

function App() {
  // 🔐 Authentication State (Simple version for now)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  // 🔍 Check authentication on app load
  useEffect(() => {
    const checkAuth = () => {
      try {
        if (AuthService.isAuthenticated()) {
          const userData = AuthService.getCurrentUser()
          setUser(userData)
          setIsAuthenticated(true)
        }
      } catch (error) {
        console.error('Auth check failed:', error)
        AuthService.logout()
        localStorage.removeItem('tezlaa-selected-branch')
      } finally {
        setLoading(false)
      }
    }

    checkAuth()
  }, [])

  // 🔑 Login function
  const login = async (username, password) => {
    try {
      const result = await AuthService.login(username, password)
      
      if (result.success) {
        // All users proceed normally without branch selection popup
        setUser(result.user)
        setIsAuthenticated(true)
        return { success: true }
      } else {
        return { 
          success: false, 
          error: result.error || 'Invalid credentials' 
        }
      }
    } catch (error) {
      console.error('Login failed:', error)
      return { 
        success: false, 
        error: 'Login failed. Please try again.' 
      }
    }
  }

  // 🏢 Branch selection handlers
  const handleBranchSelect = (selectedBranch) => {
    if (selectedBranch) {
      // Update user object with selected branch information
      const userWithBranch = {
        ...user,
        branchId: selectedBranch._id,
        selectedBranch: selectedBranch,
        branch: selectedBranch
      }
      
      // Update localStorage with the enhanced user data
      localStorage.setItem('tezlaa-user', JSON.stringify(userWithBranch))
      localStorage.setItem('tezlaa-selected-branch', JSON.stringify(selectedBranch))
      
      // Set authenticated state
      setUser(userWithBranch)
      
      toast.success(`Welcome to ${selectedBranch.name}!`, {
        duration: 3000,
        position: 'top-right',
        style: {
          background: '#10B981',
          color: '#fff',
          fontWeight: '500',
        },
        iconTheme: {
          primary: '#fff',
          secondary: '#10B981',
        },
      })
    }
  }

  // 🚪 Logout function
  const logout = async () => {
    try {
      const result = await AuthService.logout()
      
      if (result.success) {
        // Show success toast
        toast.success(result.message || 'Logout successful', {
          duration: 3000,
          position: 'top-right',
          style: {
            background: '#10B981',
            color: '#fff',
            fontWeight: '500',
          },
          iconTheme: {
            primary: '#fff',
            secondary: '#10B981',
          },
        })
      } else {
        // Show warning toast for API failure but still log out
        toast.error(result.message || 'Logout completed locally', {
          duration: 4000,
          position: 'top-right',
          style: {
            background: '#F59E0B',
            color: '#fff',
            fontWeight: '500',
          },
          iconTheme: {
            primary: '#fff',
            secondary: '#F59E0B',
          },
        })
      }
      
      // Clear all state including branch selection
      localStorage.removeItem('tezlaa-selected-branch')
      setUser(null)
      setIsAuthenticated(false)
      
      // Redirect to login page using window.location
      window.location.href = '/login'
    } catch (error) {
      console.error('Logout error:', error)
      
      // Show error toast but still log out locally
      toast.error('Logout completed locally', {
        duration: 4000,
        position: 'top-right',
        style: {
          background: '#F59E0B',
          color: '#fff',
          fontWeight: '500',
        },
        iconTheme: {
          primary: '#fff',
          secondary: '#F59E0B',
        },
      })
      
      // Clear all state including branch selection
      localStorage.removeItem('tezlaa-selected-branch')
      setUser(null)
      setIsAuthenticated(false)
      
      // Redirect to login page using window.location
      window.location.href = '/login'
    }
  }

  // ⏳ Loading screen
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading TEZLAA...</p>
        </div>
      </div>
    )
  }

  // Determine default route based on user role
  const getDefaultRoute = () => {
    const userRole = user?.role
    if (userRole === 'admin' || userRole === 'super_admin' || userRole === 'superadmin') {
      return '/admin/dashboard'
    } else if (userRole === 'manager') {
      return '/admin/dashboard'
    } else if (userRole === 'warehouse') {
      return '/admin/dashboard'
    } else if (userRole === 'barista') {
      return '/barista'
    } else {
      return '/pos'
    }
  }

  // 🏠 Main Application Routes
  return (
    <div className="min-h-screen bg-gray-50">
      <Routes>
        {/* 🧾 Public E-Receipt Route - No authentication required */}
        <Route path="/e-receipt/:orderId" element={<EReceiptView />} />
        
        {/* 🔐 Auth Routes */}
        <Route path="/login" element={
          isAuthenticated ? <Navigate to={getDefaultRoute()} replace /> : <Login onLogin={login} />
        } />
        
        {/* 🏠 Default Route - Redirect to login if not authenticated, otherwise redirect based on role */}
        <Route path="/" element={
          isAuthenticated ? <Navigate to={getDefaultRoute()} replace /> : <Navigate to="/login" replace />
        } />
        
        {/* 📊 Admin Routes - Role-based access control */}
        <Route path="/admin" element={
          isAuthenticated ? <AdminRoute user={user} onLogout={logout} onBranchSelect={handleBranchSelect} /> : <Navigate to="/login" replace />
        }>
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="sales" element={<Sales />} />
          <Route path="analytics" element={<AnalyticsDashboard />} />
          <Route path="analytics/:tab" element={<AnalyticsDashboard />} />
          <Route path="products" element={< AllProducts />} />
          <Route path="suppliers" element={<Suppliers />} />
          <Route path="customers" element={<Customers />} />
          {/* <Route path="promotions" element={<Promotions />} /> */}
          <Route path="reports" element={<Reports />} />
          
          {/* Individual Report Routes */}
          <Route path="reports/sales-summary" element={<SalesSummaryReport />} />
          <Route path="reports/payments" element={<PaymentsReport />} />
          <Route path="reports/product-summary" element={<ProductSummaryReport />} />
          <Route path="reports/raw-material-summary" element={<RawMaterialSummaryReport />} />
          <Route path="reports/product-margin" element={<ProductMarginReport />} />
          <Route path="reports/stock-alerts" element={<StockAlertsReport />} />
          <Route path="reports/suppliers" element={<SuppliersReport />} />
          <Route path="reports/customers" element={<CustomersReport />} />
          <Route path="reports/employees" element={<EmployeesReport />} />
          <Route path="reports/salary-reports" element={<SalaryReports />} />
          <Route path="reports/product-sales" element={<ProductSalesReport />} />
          <Route path="reports/stock-expiry" element={<StockExpiryReport />} />
          <Route path="reports/customer-returns" element={<CustomerReturnsReport />} />
          <Route path="reports/leave-reports" element={<LeaveReports />} />
          <Route path="reports/supplier-returns" element={<SupplierReturnsReport />} />
          <Route path="reports/wastage-report" element={<WastageReport />} />
          <Route path="reports/profit-report" element={<ProfitReport />} />
          <Route path="reports/service-charge" element={<ServiceChargeReport />} />
          <Route path="reports/product-wise-sales" element={<ProductWiseSalesReport />} />
          <Route path="reports/negative-stock-report" element={<NegativeStockReport />} />
          <Route path="warehouse" element={<Warehouse />} />
          <Route path="config/settings" element={<ConfigSettings />} />
          <Route path="roles/branches" element={<Branches />} />
          <Route path="roles/users" element={<Users />} />
          <Route path="products/category" element={<Categories />} />
          <Route path="products/units" element={<Units />} />
          <Route path="products/raw-materials" element={<RawMaterials />} />
          <Route path="products/raw-materials/inventory" element={<RawMaterialsInventory />} />
          <Route path="products/products-inventory" element={<BranchProducts />} />
          <Route path="products/recipes" element={<Recipes />} />
          <Route path="purchase-order/all" element={<AllPurchaseOrders />} />
          <Route path="purchase-order/create" element={<CreatePurchaseOrder />} />
          <Route path="grn/all" element={<AllGRN />} />
          <Route path="grn/create" element={<CreateGRN />} />
          <Route path="grn/payments" element={<GRNPayments />} />
          <Route path="warehouse/productions" element={<WarehouseProductions />} />
          <Route path="warehouse/transfer" element={<ProductsTransfer />} />
          <Route path="returns/supplier-returns" element={<SupplierReturn />} />
          <Route path="returns/customer-returns" element={<CustomerReturn />} />
          <Route path="products/stock-alerts" element={<StockAlerts />} />
          <Route path="products/batch-inventory" element={<BatchInventoryManagement />} />
          <Route path="products/vip-rooms" element={<VIPRooms />} />
          <Route path="tables" element={<Tables />} />
          <Route path="customers/credit-customers" element={<CreditCustomers />} />
          <Route path="customers/customers-group" element={<CustomerGroups />} />
          <Route path="promotions/all" element={<AllPromotions />} />
          <Route path="promotions/create" element={<CreatePromotion />} />
          <Route path="promotions/edit/:id" element={<EditPromotion />} />
          <Route path="promotions/sms-campaigns" element={<AllSMSCampaigns />} />
          <Route path="hr/jobs" element={<JobManagement />} />
          <Route path="hr/employees" element={<EmployeeManagement />} />
          <Route path="hr/shifts" element={<ShiftManagement />} />
          <Route path="hr/employee-shifts" element={<EmployeeShifts />} />
          <Route path="hr/leave-management" element={<LeaveManagement />} />
          <Route path="hr/attendance" element={<Attendance />} />
          <Route path="hr/payroll-management" element={<PayrollManagement />} />
          <Route path="hr/transport-claims" element={<TransportClaims />} />
        </Route>

        {/* 🏪 POS Routes (for later) */}
        <Route path="/pos" element={
          isAuthenticated ? <POSLayout user={user} onLogout={logout} /> : <Navigate to="/login" replace />
        }>
          {/* <Route index element={<POSSystem />} /> */}
        </Route>

        {/* 👨‍🍳 Barista Routes */}
        <Route path="/barista" element={
          isAuthenticated ? <BaristaScreen user={user} onLogout={logout} /> : <Navigate to="/login" replace />
        } />
        <Route path="/barista/demo" element={
          isAuthenticated ? <BaristaDemo user={user} /> : <Navigate to="/login" replace />
        } />
        <Route path="/barista-demo" element={
          isAuthenticated ? <BaristaDemo user={user} /> : <Navigate to="/login" replace />
        } />

        {/* 🔐 Auth Routes */}
        <Route path="/login" element={<LoginRoute isAuthenticated={isAuthenticated} user={user} onLogin={login} />} />
        
        {/* 🚫 Catch all - redirect to login if not authenticated, otherwise redirect based on user role */}
        <Route path="*" element={
          isAuthenticated ? <Navigate to={getDefaultRoute()} replace /> : <Navigate to="/login" replace />
        } />
      </Routes>
    </div>
  )
}

export default App